/**
 * NoteRenderer - Renders note annotations as SVG
 */

import type { Note, NotePosition } from '../../../parser/types';
import type { ParticipantLayout } from '../layout/types';

/**
 * Note layout information for rendering
 */
export interface NoteLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  position: NotePosition;
}

export interface NoteRenderOptions {
  /**
   * Width of notes
   */
  noteWidth: number;
  /**
   * Padding inside notes
   */
  notePadding: number;
  /**
   * Horizontal offset for left/right positioned notes
   */
  noteHorizontalOffset: number;
  /**
   * CSS class for notes
   */
  noteClass: string;
}

/**
 * Default rendering options for notes
 */
export const DEFAULT_NOTE_OPTIONS: NoteRenderOptions = {
  noteWidth: 180,
  notePadding: 12,
  noteHorizontalOffset: 20,
  noteClass: 'note-box',
};

/**
 * Calculates the layout for a note based on participant positions
 */
export function calculateNoteLayout(
  note: Note,
  participantLayouts: Map<string, ParticipantLayout>,
  yPosition: number,
  options: NoteRenderOptions = DEFAULT_NOTE_OPTIONS
): NoteLayout | null {
  // Get layouts for all target participants
  const targetLayouts: ParticipantLayout[] = [];
  for (const participantName of note.participants) {
    const layout = participantLayouts.get(participantName);
    if (layout) {
      targetLayouts.push(layout);
    }
  }

  if (targetLayouts.length === 0) {
    return null; // No valid participants found
  }

  let x: number;
  let width: number;

  if (note.position === 'over') {
    // Position over participant(s)
    // For multiple participants, span across them
    const leftmost = Math.min(...targetLayouts.map((l) => l.x));
    const rightmost = Math.max(...targetLayouts.map((l) => l.x + l.width));

    x = leftmost;
    width = rightmost - leftmost;

    // Ensure minimum width
    if (width < options.noteWidth) {
      const centerX = (leftmost + rightmost) / 2;
      x = centerX - options.noteWidth / 2;
      width = options.noteWidth;
    }
  } else {
    // Position to the left or right of the first participant
    const firstLayout = targetLayouts[0];
    if (!firstLayout) {
      return null; // Should not happen after length check, but satisfy linter
    }

    width = options.noteWidth;
    if (note.position === 'left') {
      x = firstLayout.x - width - options.noteHorizontalOffset;
    } else {
      // position === 'right'
      x = firstLayout.x + firstLayout.width + options.noteHorizontalOffset;
    }
  }

  // Check if we need to expand width for long words (including hyphenated words)
  const words = note.text.split(/\s+/);
  // Split hyphenated words to find the longest segment
  const allSegments = words.flatMap((w) => w.split('-'));
  const longestSegment = Math.max(...allSegments.map((s) => s.length));
  const minWidthForLongestWord = longestSegment * 9 + 40; // 9px per char + 40px horizontal padding

  if (minWidthForLongestWord > width) {
    const oldWidth = width;
    width = Math.min(minWidthForLongestWord, 500); // Cap at 500px max

    // Re-center the note if we expanded the width
    if (note.position === 'over') {
      x = x - (width - oldWidth) / 2;
    }
  }

  // Calculate height based on text wrapping
  const maxCharsPerLine = Math.floor((width - 20) / 9); // Subtract horizontal padding
  const lineCount = calculateLineCount(note.text, maxCharsPerLine);
  const lineHeight = 18;
  const verticalPadding = 24; // Top and bottom padding
  const height = lineCount * lineHeight + verticalPadding;

  return {
    x,
    y: yPosition,
    width,
    height,
    text: note.text,
    position: note.position,
  };
}

/**
 * Calculates how many lines the text will take when wrapped
 */
function calculateLineCount(text: string, maxCharsPerLine: number): number {
  if (text.length <= maxCharsPerLine) {
    return 1;
  }

  const words = text.split(/\s+/);
  let lines = 0;
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? currentLine + ' ' + word : word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines++;
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines++;
  }

  return lines > 0 ? lines : 1;
}

/**
 * Renders a note annotation as SVG
 */
export function renderNote(
  noteLayout: NoteLayout,
  options: NoteRenderOptions = DEFAULT_NOTE_OPTIONS
): string {
  const { x, y, width, height, text } = noteLayout;
  const parts: string[] = [];

  parts.push('<g class="note">');

  // Note box
  parts.push(
    `  <rect x="${x}" y="${y}" width="${width}" height="${height}" ` +
      `class="${options.noteClass}" rx="4" />`
  );

  // Note text (centered with wrapping)
  const textX = x + width / 2;
  const textY = y + height / 2;

  // Wrap text if too long (approximate 9px per character, with padding)
  const maxCharsPerLine = Math.floor((width - 20) / 9);
  const lines = wrapText(text, maxCharsPerLine);
  const lineHeight = 18;
  const totalTextHeight = lines.length * lineHeight;
  const startY = textY - totalTextHeight / 2 + lineHeight / 2;

  if (lines.length === 1) {
    // Single line
    parts.push(
      `  <text x="${textX}" y="${textY}" class="note-text" ` +
        `text-anchor="middle" dominant-baseline="middle">${escapeXml(text)}</text>`
    );
  } else {
    // Multiple lines with tspan
    parts.push(
      `  <text x="${textX}" y="${startY}" class="note-text" text-anchor="middle">`
    );
    lines.forEach((line, index) => {
      const dy = index === 0 ? 0 : lineHeight;
      parts.push(
        `    <tspan x="${textX}" dy="${dy}">${escapeXml(line)}</tspan>`
      );
    });
    parts.push('  </text>');
  }

  parts.push('</g>');

  return parts.join('\n');
}

/**
 * Wraps text into multiple lines based on character limit
 */
function wrapText(text: string, maxCharsPerLine: number): string[] {
  if (text.length <= maxCharsPerLine) {
    return [text];
  }

  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? currentLine + ' ' + word : word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [text];
}

/**
 * Renders multiple notes for a step
 */
export function renderNotes(
  notes: Note[],
  participantLayouts: Map<string, ParticipantLayout>,
  yPosition: number,
  stepIndex: number,
  options: NoteRenderOptions = DEFAULT_NOTE_OPTIONS
): string {
  if (notes.length === 0) {
    return '';
  }

  const parts: string[] = [];
  parts.push(`<g class="step-notes" data-step="${stepIndex}">`);

  for (const note of notes) {
    const layout = calculateNoteLayout(
      note,
      participantLayouts,
      yPosition,
      options
    );
    if (layout) {
      parts.push('  ' + renderNote(layout, options));
    }
  }

  parts.push('</g>');

  return parts.join('\n');
}

/**
 * Escapes XML special characters in text
 */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
